import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AdminSidebar, Icon, StatusBadge, Pagination, formatBRL } from '../../components/shared';
import api from '../../services/api';
import toast from 'react-hot-toast';

export default function AdminProdutos() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const page         = parseInt(searchParams.get('page') || '1', 10);
  const busca        = searchParams.get('busca') || '';
  const catFiltro    = searchParams.get('categoria_id') || '';
  const subcatFiltro = searchParams.get('subcategoria_id') || '';
  const statusFiltro = searchParams.get('status') || 'ativo'; // 'ativo' | 'inativo' | 'todos'
  const statusApi    = statusFiltro === 'todos' ? '' : statusFiltro;

  const [produtos, setProdutos]         = useState([]);
  const [categorias, setCategorias]     = useState([]);
  const [subcategorias, setSubcategorias] = useState([]);
  const [total, setTotal]               = useState(0);
  const [loading, setLoading]           = useState(true);
  const [selecionados, setSelecionados] = useState([]);
  const [categoriaLote, setCategoriaLote] = useState('');
  const [subcategoriaLote, setSubcategoriaLote] = useState('');
  const [subcatsLote, setSubcatsLote] = useState([]);
  const [aplicandoLote, setAplicandoLote] = useState(false);
  const [statusLoteEmAndamento, setStatusLoteEmAndamento] = useState(null); // 'ativo' | 'inativo' | null

  const carregar = async () => {
    setLoading(true);
    try {
      const [prods, cats] = await Promise.all([
        api.get('/produtos', { params: { page, limit: 50, busca, categoria_id: catFiltro, subcategoria_id: subcatFiltro, status: statusApi } }),
        api.get('/categorias'),
      ]);
      setProdutos(prods.data.dados);
      setTotal(prods.data.total);
      setCategorias(cats.data);
    } catch { toast.error('Erro ao carregar'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (!catFiltro) { setSubcategorias([]); return; }
    api.get('/subcategorias', { params: { categoria_id: catFiltro } })
      .then(r => setSubcategorias(r.data))
      .catch(() => setSubcategorias([]));
  }, [catFiltro]);

  useEffect(() => { carregar(); }, [page, busca, catFiltro, subcatFiltro, statusFiltro]);
  useEffect(() => { setSelecionados([]); }, [page, busca, catFiltro, subcatFiltro, statusFiltro]);

  const setParam = (key, value, keepPage = false) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    if (!keepPage) next.delete('page');
    setSearchParams(next);
  };

  const irParaForm = (path) => {
    const from = `/admin/produtos${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
    navigate(path, { state: { from } });
  };

  const excluir = async (id) => {
    if (!window.confirm('Inativar este produto?')) return;
    try { await api.delete(`/produtos/${id}`); toast.success('Produto inativado!'); carregar(); }
    catch { toast.error('Erro ao excluir'); }
  };

  const toggleSelecionado = (id) => {
    setSelecionados(sel => sel.includes(id) ? sel.filter(x => x !== id) : [...sel, id]);
  };
  const toggleSelecionarTodos = () => {
    setSelecionados(sel => sel.length === produtos.length ? [] : produtos.map(p => p.id));
  };

  const handleCategoriaLote = (catId) => {
    setCategoriaLote(catId);
    setSubcategoriaLote('');
    if (!catId) { setSubcatsLote([]); return; }
    api.get('/subcategorias', { params: { categoria_id: catId } })
      .then(r => setSubcatsLote(r.data))
      .catch(() => setSubcatsLote([]));
  };

  const aplicarCategoriaLote = async () => {
    if (!categoriaLote) return;
    setAplicandoLote(true);
    try {
      const { data } = await api.put('/produtos/categoria-lote', { ids: selecionados, categoria_id: categoriaLote });
      toast.success(`${data.atualizados} produto(s) atualizado(s)!`);
      setSelecionados([]); setCategoriaLote(''); setSubcategoriaLote(''); setSubcatsLote([]);
      carregar();
    } catch (err) { toast.error(err.response?.data?.erro || 'Erro ao atualizar categoria'); }
    finally { setAplicandoLote(false); }
  };

  const aplicarSubcategoriaLote = async () => {
    setAplicandoLote(true);
    try {
      const { data } = await api.put('/produtos/subcategoria-lote', {
        ids: selecionados,
        subcategoria_id: subcategoriaLote || null,
      });
      toast.success(`${data.atualizados} produto(s) atualizado(s)!`);
      setSelecionados([]); setCategoriaLote(''); setSubcategoriaLote(''); setSubcatsLote([]);
      carregar();
    } catch (err) { toast.error(err.response?.data?.erro || 'Erro ao atualizar subcategoria'); }
    finally { setAplicandoLote(false); }
  };

  const alterarStatusLote = async (status) => {
    const acao = status === 'ativo' ? 'Ativar' : 'Inativar';
    if (!window.confirm(`${acao} ${selecionados.length} produto(s) selecionado(s)?`)) return;
    setStatusLoteEmAndamento(status);
    try {
      const { data } = await api.put('/produtos/status-lote', { ids: selecionados, status });
      toast.success(`${data.atualizados} produto(s) ${status === 'ativo' ? 'ativado(s)' : 'inativado(s)'}!`);
      setSelecionados([]);
      carregar();
    } catch (err) { toast.error(err.response?.data?.erro || `Erro ao ${acao.toLowerCase()}`); }
    finally { setStatusLoteEmAndamento(null); }
  };

  const API = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

  return (
    <div className="admin-layout">
      <AdminSidebar perfil="admin_empresa" />
      <div className="main-content">
        <div className="topbar">
          <h1>Produtos</h1>
          <button className="btn btn-primary" onClick={() => irParaForm('/admin/produtos/novo')}>
            <Icon name="plus" size={16} /> Novo Produto
          </button>
        </div>
        <div className="page-content">
          <div className="table-card">
            <div className="table-header">
              <div className="filter-bar">
                <div className="search-input">
                  <Icon name="search" />
                  <input className="form-control" placeholder="Buscar produto..." value={busca}
                    onChange={e => setParam('busca', e.target.value)}
                    style={{ width: '200px', paddingLeft: '34px' }} />
                </div>
                <select className="form-control" value={catFiltro}
                  onChange={e => {
                    const next = new URLSearchParams(searchParams);
                    if (e.target.value) next.set('categoria_id', e.target.value); else next.delete('categoria_id');
                    next.delete('subcategoria_id');
                    next.delete('page');
                    setSearchParams(next);
                  }} style={{ width: '160px' }}>
                  <option value="">Todas categorias</option>
                  {categorias.map(c => <option key={c.id} value={c.id}>{c.descricao}</option>)}
                </select>
                {subcategorias.length > 0 && (
                  <select className="form-control" value={subcatFiltro}
                    onChange={e => setParam('subcategoria_id', e.target.value)} style={{ width: '160px' }}>
                    <option value="">Todas subcategorias</option>
                    {subcategorias.map(s => <option key={s.id} value={s.id}>{s.descricao}</option>)}
                  </select>
                )}
                <select className="form-control" value={statusFiltro}
                  onChange={e => setParam('status', e.target.value)} style={{ width: '130px' }}>
                  <option value="todos">Todos status</option>
                  <option value="ativo">Ativos</option>
                  <option value="inativo">Inativos</option>
                </select>
              </div>
              <span style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>{total} produto(s)</span>
            </div>

            {selecionados.length > 0 && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 16px',
                background: 'var(--primary-light, #fff3ed)', borderBottom: '1px solid var(--border)'
              }}>
                <span style={{ fontSize: '.85rem', fontWeight: 600 }}>{selecionados.length} selecionado(s)</span>
                <select className="form-control" value={categoriaLote}
                  onChange={e => handleCategoriaLote(e.target.value)} style={{ width: '180px' }}>
                  <option value="">Atribuir categoria...</option>
                  {categorias.map(c => <option key={c.id} value={c.id}>{c.descricao}</option>)}
                </select>
                {categoriaLote && (
                  <button className="btn btn-primary btn-sm" onClick={aplicarCategoriaLote}
                    disabled={aplicandoLote}>
                    {aplicandoLote ? 'Aplicando...' : 'Aplicar categoria'}
                  </button>
                )}
                {subcatsLote.length > 0 && (
                  <>
                    <select className="form-control" value={subcategoriaLote}
                      onChange={e => setSubcategoriaLote(e.target.value)} style={{ width: '180px' }}>
                      <option value="">Atribuir subcategoria...</option>
                      {subcatsLote.map(s => <option key={s.id} value={s.id}>{s.descricao}</option>)}
                    </select>
                    <button className="btn btn-primary btn-sm" onClick={aplicarSubcategoriaLote}
                      disabled={aplicandoLote}>
                      {aplicandoLote ? 'Aplicando...' : 'Aplicar subcategoria'}
                    </button>
                  </>
                )}
                <button className="btn btn-success btn-sm" onClick={() => alterarStatusLote('ativo')}
                  disabled={!!statusLoteEmAndamento}>
                  {statusLoteEmAndamento === 'ativo' ? 'Ativando...' : 'Ativar selecionados'}
                </button>
                <button className="btn btn-danger btn-sm" onClick={() => alterarStatusLote('inativo')}
                  disabled={!!statusLoteEmAndamento}>
                  {statusLoteEmAndamento === 'inativo' ? 'Inativando...' : 'Inativar selecionados'}
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => setSelecionados([])}>Cancelar</button>
              </div>
            )}

            {loading ? <div className="loading-center"><div className="spinner" /></div> : (
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: '30px' }}>
                        <input type="checkbox"
                          checked={produtos.length > 0 && selecionados.length === produtos.length}
                          onChange={toggleSelecionarTodos} />
                      </th>
                      <th>Produto</th><th>Categoria</th><th>Subcategoria</th><th>Preço</th><th>Destaque</th><th>Status</th><th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {produtos.map(p => (
                      <tr key={p.id}>
                        <td>
                          <input type="checkbox"
                            checked={selecionados.includes(p.id)}
                            onChange={() => toggleSelecionado(p.id)} />
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            {p.foto_url
                              ? <img src={`${API}${p.foto_url}`} alt="" style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover' }} />
                              : <div style={{ width: 40, height: 40, borderRadius: 8, background: '#f0ede8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>🍽</div>
                            }
                            <div>
                              <div style={{ fontWeight: 600 }}>{p.descricao}</div>
                              {p.codigo_erp && <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>ERP: {p.codigo_erp}</div>}
                            </div>
                          </div>
                        </td>
                        <td style={{ fontSize: '.85rem' }}>{p.categoria}</td>
                        <td>
          <div style={{ fontWeight: 700, color: 'var(--primary)' }}>{formatBRL(p.preco_promocional || p.preco_venda)}</div>
          {p.preco_promocional && <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', textDecoration: 'line-through' }}>{formatBRL(p.preco_venda)}</div>}
        </td>
                        <td>{p.destaque ? <span className="badge badge-orange">⭐ Destaque</span> : '—'}</td>
                        <td><StatusBadge status={p.status} /></td>
                        <td>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button className="btn btn-icon btn-sm" onClick={() => irParaForm(`/admin/produtos/${p.id}/editar`)}><Icon name="edit" size={14} /></button>
                            <button className="btn btn-icon btn-sm" onClick={() => excluir(p.id)}
                              style={{ color: 'var(--danger)' }}><Icon name="trash" size={14} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {!produtos.length && (
                      <tr><td colSpan={7}>
                        <div className="empty-state"><Icon name="package" /><h3>Nenhum produto cadastrado</h3></div>
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
            <div className="table-footer">
              <span>Mostrando {produtos.length} de {total}</span>
              <Pagination page={page} total={total} limit={50} onChange={p => setParam('page', String(p), true)} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
